import React, { useState } from 'react';
import { publicService } from '../services/publicService';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { formatRut } from '../utils/rut';

const PublicHome: React.FC = () => {
  const navigate = useNavigate();
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [empresaRut, setEmpresaRut] = useState('');
  const [adminNombre, setAdminNombre] = useState('');
  const [adminApellido, setAdminApellido] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleNombreChange = (val: string) => {
    setEmpresaNombre(val);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!empresaNombre.trim()) { setError('El nombre de la empresa es requerido'); return; }
    if (!empresaRut.trim()) { setError('El RUT de la empresa es requerido'); return; }
    if (!adminEmail.trim()) { setError('El email del administrador es requerido'); return; }
    if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    const normalizeRut = (v: string) => v.replace(/[.-]/g, '').toUpperCase();
    try {
      setIsSubmitting(true);
      const res = await publicService.signupCompany({
        empresaNombre: empresaNombre.trim(),
        empresaRut: normalizeRut(empresaRut.trim()),
        adminNombre: adminNombre.trim() || undefined,
        adminApellido: adminApellido.trim() || undefined,
        adminEmail: adminEmail.trim(),
        password,
      });
      setSuccess(res.mensaje || 'Empresa creada exitosamente.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'No se pudo crear la empresa';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size="xxxxl" />
          <div className="flex items-center space-x-6">
            <Link to="/login" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Acceder</Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-indigo-50 via-white to-blue-50">
          <div className="max-w-7xl mx-auto px-4 py-16">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6">
                Control Total de <span className="text-indigo-600">Limpieza</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                La plataforma para gestionar la limpieza en todo tipo de instalaciones.
                Organiza tareas, monitorea en tiempo real y mejora la calidad del servicio.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => document.getElementById('registro')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-lg font-semibold"
                >
                  Comenzar Gratis
                </button>
                <Link
                  to="/login"
                  className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-gray-400 text-lg font-semibold"
                >
                  Ver Demo
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Estadísticas eliminadas temporalmente: aún no estamos en producción */}

        {/* Características Principales */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Características Principales</h2>
              <p className="text-xl text-gray-600">Todo lo que necesitas para gestionar la limpieza de tu instalación</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Gestión de Tareas</h3>
                <p className="text-gray-600">Crea y asigna tareas de limpieza por pisos y áreas. Establece horarios y prioridades para cada actividad.</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Monitoreo en Tiempo Real</h3>
                <p className="text-gray-600">Sigue el progreso de las tareas en tiempo real. Recibe notificaciones instantáneas por WhatsApp.</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Reportes Detallados</h3>
                <p className="text-gray-600">Genera reportes completos de productividad, cumplimiento y calidad. Analiza tendencias y mejora procesos.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Beneficios */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">¿Por qué elegir Control Limpio?</h2>
              <p className="text-xl text-gray-600">Descubre los beneficios que obtendrás al implementar nuestra solución</p>
            </div>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Reducción del 40% en tiempo de gestión</h3>
                    <p className="text-gray-600">Automatiza procesos y elimina tareas manuales repetitivas</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Mejora del 60% en cumplimiento</h3>
                    <p className="text-gray-600">Seguimiento detallado y alertas automáticas garantizan la ejecución</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Ahorro del 35% en costos operativos</h3>
                    <p className="text-gray-600">Optimización de recursos y eliminación de ineficiencias</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-8 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Implementación Rápida</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <span className="text-gray-700">Registro gratuito en 2 minutos</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <span className="text-gray-700">Configuración inicial automática</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <span className="text-gray-700">Capacitación del equipo incluida</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                    <span className="text-gray-700">Soporte técnico 24/7 disponible</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonios */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Lo que dicen nuestros clientes</h2>
              <p className="text-xl text-gray-600">Testimonios de organizaciones que confían en Control Limpio</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-indigo-600 font-semibold">MC</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">María Cortés</div>
                    <div className="text-sm text-gray-600">Directora de Servicios</div>
                  </div>
                </div>
                <p className="text-gray-700">"Control Limpio transformó completamente nuestra gestión de limpieza. El monitoreo en tiempo real nos permite garantizar la calidad del servicio."</p>
                <div className="flex text-yellow-400 mt-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-indigo-600 font-semibold">JL</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Juan López</div>
                    <div className="text-sm text-gray-600">Gerente de Operaciones</div>
                  </div>
                </div>
                <p className="text-gray-700">"La implementación fue muy sencilla y el equipo de soporte nos ayudó en todo el proceso. Los reportes nos han permitido optimizar nuestros procesos."</p>
                <div className="flex text-yellow-400 mt-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-indigo-600 font-semibold">AG</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Ana García</div>
                    <div className="text-sm text-gray-600">Supervisora de Limpieza</div>
                  </div>
                </div>
                <p className="text-gray-700">"Las alertas por WhatsApp han mejorado significativamente la comunicación con nuestro equipo. Ahora todos están informados en tiempo real."</p>
                <div className="flex text-yellow-400 mt-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Formulario de Registro */}
        <section id="registro" className="py-16 bg-indigo-600">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Comienza Hoy Mismo</h2>
              <p className="text-xl text-indigo-100">Únete a más de 150 organizaciones que ya confían en Control Limpio</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-lg font-semibold text-gray-900 mb-6">Crear empresa</div>
              {error && (
                <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>
              )}
              {success && (
                <div className="mb-4 p-3 rounded bg-green-50 text-green-700 text-sm">{success}</div>
              )}
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Nombre de la empresa</label>
                  <input
                    type="text"
                    value={empresaNombre}
                    onChange={(e) => handleNombreChange(e.target.value)}
                    placeholder="Ej: Instalación Central"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">RUT empresa</label>
                  <input
                    type="text"
                    value={empresaRut}
                    onChange={(e) => setEmpresaRut(formatRut(e.target.value))}
                    placeholder="12.345.678-9"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Nombre admin</label>
                    <input
                      type="text"
                      value={adminNombre}
                      onChange={(e) => setAdminNombre(e.target.value)}
                      placeholder="Nombre"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Apellido admin</label>
                    <input
                      type="text"
                      value={adminApellido}
                      onChange={(e) => setAdminApellido(e.target.value)}
                      placeholder="Apellido"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Email admin</label>
                  <input
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@tuempresa.com"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Contraseña</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 font-semibold text-lg"
                >
                  {isSubmitting ? 'Creando…' : 'Crear empresa gratis'}
                </button>
                <p className="text-xs text-gray-500 text-center">Al crear una empresa aceptas nuestras políticas de privacidad y términos de servicio.</p>
              </form>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <Logo size="md" showTagline={false} className="text-white" />
              <p className="text-gray-400 mt-4">La plataforma para gestionar la limpieza en todo tipo de instalaciones.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Producto</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/login" className="hover:text-white">Características</Link></li>
                <li><Link to="/login" className="hover:text-white">Planes</Link></li>
                <li><Link to="/login" className="hover:text-white">Integraciones</Link></li>
                <li><Link to="/login" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Soporte</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/login" className="hover:text-white">Centro de ayuda</Link></li>
                <li><Link to="/login" className="hover:text-white">Documentación</Link></li>
                <li><Link to="/login" className="hover:text-white">Contacto</Link></li>
                <li><Link to="/login" className="hover:text-white">Estado del servicio</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Empresa</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/privacy" className="hover:text-white">Privacidad</Link></li>
                <li><Link to="/terms" className="hover:text-white">Términos</Link></li>
                <li><Link to="/login" className="hover:text-white">Sobre nosotros</Link></li>
                <li><Link to="/login" className="hover:text-white">Blog</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>© {new Date().getFullYear()} Control Limpio. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicHome;
